import { Effect } from 'effect';
import {
  BadgeCheck,
  ClipboardList,
  ImagePlus,
  LayoutGrid,
  Shield,
  Type,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { StudioTool, StudioToolCategory } from './tool';

/**
 * The dock map — the one place that says where a tool lives in the canvas
 * layout. Every visible tool has exactly one home: a dock group, the share
 * dialog (Share link / Export), or the home's Import list (`ai` tools have
 * the host's own entry point instead). Pure functions over the tool list a
 * host already filtered (`useStudioTools().tools`), so host extras and hidden
 * categories need no second rule here.
 */

/**
 * The host tool behind the share dialog's Share link section. It has its own
 * place there, so it is never listed as an export or import format.
 */
export const SHARE_LINK_TOOL_ID = 'pdf-to-link';

export type StudioDockGroupId = 'text' | 'marks' | 'pages' | 'forms' | 'protect' | 'fix' | 'check';

export interface StudioDockSection {
  /** Section heading; empty for a group that reads as one list. */
  title: string;
  tools: StudioTool[];
}

export interface StudioDockGroup {
  id: StudioDockGroupId;
  title: string;
  description: string;
  icon: LucideIcon;
  sections: StudioDockSection[];
}

/** One group of the catalogue a host lists: a dock group, Share, Import or Export. */
export interface StudioToolGroup {
  id: string;
  title: string;
  tools: StudioTool[];
}

interface DockSectionDefinition {
  title: string;
  toolIds: string[];
  /**
   * Tools the map does not name that also belong here, listed ahead of the
   * named ones — a new registry or host tool shows up in the dock without an
   * edit to the map.
   */
  unnamed?: (tool: StudioTool) => boolean;
}

interface DockGroupDefinition {
  id: StudioDockGroupId;
  title: string;
  description: string;
  icon: LucideIcon;
  sections: DockSectionDefinition[];
}

const inCategory = (category: StudioToolCategory) => (tool: StudioTool) =>
  tool.category === category;

// Convert tools are sorted by direction, not by category alone. One that works
// on the open document is an export; one that needs no document and makes a
// PDF is an import.
const isFormatTool = (tool: StudioTool) =>
  tool.category === 'convert' && tool.id !== SHARE_LINK_TOOL_ID;

const isExportTool = (tool: StudioTool) => isFormatTool(tool) && tool.requiresDocument;

const isImportTool = (tool: StudioTool) =>
  isFormatTool(tool) && !tool.requiresDocument && tool.id.endsWith('-to-pdf');

// A convert tool that is neither (it takes its own file and makes something
// other than a PDF) would otherwise be listed nowhere.
const isStrayConvertTool = (tool: StudioTool) =>
  isFormatTool(tool) && !isExportTool(tool) && !isImportTool(tool);

const DOCK_GROUPS: DockGroupDefinition[] = [
  {
    id: 'text',
    title: 'Text',
    description: 'Change or add words on the page',
    icon: Type,
    sections: [
      { title: 'Edit', toolIds: ['edit-text', 'add-text-to-pdf'] },
      { title: 'Mark up', toolIds: ['highlight'] },
      { title: 'More', toolIds: [], unnamed: inCategory('edit') },
    ],
  },
  {
    id: 'marks',
    title: 'Images & marks',
    description: 'Place images, signatures and stamps',
    icon: ImagePlus,
    sections: [
      { title: 'Place', toolIds: ['add-image-to-pdf', 'sign'] },
      { title: 'Stamp', toolIds: ['watermark', 'page-numbers', 'bates'] },
    ],
  },
  {
    id: 'pages',
    title: 'Pages',
    description: 'Arrange, combine and split pages',
    icon: LayoutGrid,
    sections: [
      { title: 'Arrange', toolIds: ['organize', 'rotate', 'crop'] },
      { title: 'Combine & split', toolIds: ['merge', 'split', 'remove-blank-pages'] },
      { title: 'More', toolIds: [], unnamed: inCategory('organize') },
    ],
  },
  {
    id: 'forms',
    title: 'Forms',
    description: 'Fill, flatten and clean up fields',
    icon: ClipboardList,
    sections: [{ title: '', toolIds: ['fill-pdf-form', 'flatten', 'remove-pdf-annotations'] }],
  },
  {
    id: 'protect',
    title: 'Protect',
    description: 'Remove what must not leave',
    icon: Shield,
    sections: [
      { title: '', toolIds: ['redact', 'metadata'] },
      { title: 'More', toolIds: [], unnamed: inCategory('security') },
    ],
  },
  {
    id: 'fix',
    title: 'Fix',
    description: 'Shrink, repair and make searchable',
    icon: Wrench,
    sections: [
      { title: '', toolIds: ['compress', 'repair-pdf', 'ocr', 'handwriting-ocr', 'compare'] },
      { title: 'More', toolIds: [], unnamed: isStrayConvertTool },
    ],
  },
  {
    id: 'check',
    title: 'Check',
    description: 'Test against PDF standards',
    icon: BadgeCheck,
    // The checkers are host tools, so the list is filled by category. PDF to
    // HTML takes its own file, as they do, so it closes the same list.
    sections: [{ title: '', toolIds: ['pdf-to-html'], unnamed: inCategory('analyze') }],
  },
];

const MAPPED_TOOL_IDS = new Set(
  DOCK_GROUPS.flatMap((group) => group.sections.flatMap((section) => section.toolIds)),
);

// A tool the map names keeps that one home, whatever else it would match.
const unnamedTools = (tools: StudioTool[], belongs: (tool: StudioTool) => boolean) =>
  tools.filter((tool) => !MAPPED_TOOL_IDS.has(tool.id) && belongs(tool));

/**
 * Group the visible tools for the dock. Import and export tools, the share
 * link tool and `ai` tools are not in the dock.
 */
export function buildDockGroups(tools: StudioTool[]): StudioDockGroup[] {
  return Effect.runSync(buildDockGroupsEffect(tools));
}

export function buildDockGroupsEffect(tools: StudioTool[]): Effect.Effect<StudioDockGroup[]> {
  return Effect.sync(() => {
    const byId = new Map(tools.map((tool) => [tool.id, tool]));
    return DOCK_GROUPS.map(({ sections, ...group }) => ({
      ...group,
      sections: sections
        .map(({ title, toolIds, unnamed }) => ({
          title,
          tools: [
            ...(unnamed ? unnamedTools(tools, unnamed) : []),
            ...toolIds.flatMap((id) => byId.get(id) ?? []),
          ],
        }))
        .filter((section) => section.tools.length > 0),
    })).filter((group) => group.sections.length > 0);
  });
}

/** Tools the share dialog lists under Export (document → another format). */
export function exportTools(tools: StudioTool[]): StudioTool[] {
  return Effect.runSync(exportToolsEffect(tools));
}

export function exportToolsEffect(tools: StudioTool[]): Effect.Effect<StudioTool[]> {
  return Effect.sync(() => unnamedTools(tools, isExportTool));
}

/**
 * Tools that turn another file into a PDF (no document needed; the id ends in
 * `-to-pdf`). A no-document convert tool that makes anything else is a dock
 * tool, so the Import list only ever holds imports.
 */
export function importTools(tools: StudioTool[]): StudioTool[] {
  return Effect.runSync(importToolsEffect(tools));
}

export function importToolsEffect(tools: StudioTool[]): Effect.Effect<StudioTool[]> {
  return Effect.sync(() => unnamedTools(tools, isImportTool));
}

/**
 * Every visible tool under its one home, in the order a host lists them (the
 * home's All tools, the host's tool search): the dock groups, then Share,
 * Import and Export. `ai` tools have the host's own entry point instead.
 */
export function buildToolGroups(tools: StudioTool[]): StudioToolGroup[] {
  return Effect.runSync(buildToolGroupsEffect(tools));
}

export function buildToolGroupsEffect(tools: StudioTool[]): Effect.Effect<StudioToolGroup[]> {
  return Effect.gen(function* () {
    const dock = yield* buildDockGroupsEffect(tools);
    const imports = yield* importToolsEffect(tools);
    const exports = yield* exportToolsEffect(tools);
    const groups: StudioToolGroup[] = [
      ...dock.map(({ id, title, sections }) => ({
        id,
        title,
        tools: sections.flatMap((section) => section.tools),
      })),
      { id: 'share', title: 'Share', tools: tools.filter((tool) => tool.id === SHARE_LINK_TOOL_ID) },
      { id: 'import', title: 'Import', tools: imports },
      { id: 'export', title: 'Export', tools: exports },
    ];
    return groups.filter((group) => group.tools.length > 0);
  });
}
